/**
 * Exporte le cahier de préconisation en fichier HTML autonome
 * Le fichier généré contient tout le CSS inline et peut être ouvert hors ligne
 * Le client peut l'imprimer en PDF depuis son navigateur
 */

export function exportToHTML(project) {
  try {
    console.log('[exportHTML] Starting export with project:', project?.slug);

    if (!project) {
      console.error('[exportHTML] No project provided');
      alert('Erreur : Aucun projet à exporter');
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
      } catch (e) {
        console.error('[exportHTML] Date formatting error:', e);
        return dateStr;
      }
    };

    const formatPrice = (price) => {
      if (typeof price !== 'number') return price;
      try {
        return new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(price);
      } catch (e) {
        console.error('[exportHTML] Price formatting error:', e);
        return `${price}€`;
      }
    };

    console.log('[exportHTML] Generating HTML content...');

    const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${project.title || 'Cahier de Préconisation'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #1f2937;
      background: #f9fafb;
      font-size: 15px;
    }

    .container { max-width: 1100px; margin: 0 auto; padding: 0; }

    /* Typography */
    h1 {
      font-size: 2.75rem;
      font-weight: 700;
      margin-bottom: 0.75rem;
      line-height: 1.2;
    }
    h2 {
      color: #d13d4b;
      font-size: 2rem;
      margin: 3rem 0 1.5rem;
      border-bottom: 3px solid #d13d4b;
      padding-bottom: 0.75rem;
      font-weight: 700;
      page-break-after: avoid;
    }
    h3 {
      color: #111827;
      font-size: 1.5rem;
      margin: 2rem 0 1rem;
      font-weight: 600;
    }
    h4 {
      color: #374151;
      font-size: 1.125rem;
      margin: 1.5rem 0 0.75rem;
      font-weight: 600;
    }
    p { margin-bottom: 1rem; color: #4b5563; line-height: 1.7; }
    ul, ol { margin-left: 1.75rem; margin-bottom: 1.25rem; }
    li { margin-bottom: 0.5rem; color: #4b5563; line-height: 1.6; }
    strong { color: #111827; font-weight: 600; }

    /* Hero Section */
    .hero {
      background: linear-gradient(135deg, #1a1a1a 0%, #d13d4b 50%, #1a1a1a 100%);
      color: white;
      padding: 4rem 3rem;
      text-align: center;
      margin-bottom: 0;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
    .hero h1 { color: white; }
    .hero-subtitle { font-size: 1.25rem; margin-bottom: 1.5rem; opacity: 0.95; }
    .hero-badge {
      display: inline-block;
      background: rgba(255,255,255,0.2);
      padding: 0.4rem 1rem;
      border-radius: 20px;
      font-size: 0.9rem;
      margin-bottom: 1rem;
      font-weight: 500;
    }
    .hero-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;
      margin-top: 2.5rem;
      padding-top: 2rem;
      border-top: 1px solid rgba(255,255,255,0.3);
    }
    .stat { text-align: center; }
    .stat-value {
      font-size: 2rem;
      font-weight: 700;
      display: block;
      margin-bottom: 0.25rem;
    }
    .stat-label {
      font-size: 0.9rem;
      opacity: 0.9;
    }

    /* Sections */
    .section {
      page-break-inside: avoid;
      margin-bottom: 2rem;
      padding: 2.5rem 3rem;
      background: #ffffff;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .section:first-of-type { margin-top: 0; }

    /* Cards */
    .card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 1.5rem;
      margin: 1rem 0;
      background: #f9fafb;
    }
    .card-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1.5rem;
      margin: 1.5rem 0;
    }

    /* Formulas */
    .formula-card {
      border: 2px solid #d13d4b;
      border-radius: 12px;
      padding: 2rem;
      margin: 2rem 0;
      page-break-inside: avoid;
      background: white;
      position: relative;
    }
    .formula-header {
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 1.5rem;
      margin-bottom: 1.5rem;
    }
    .formula-name {
      font-size: 1.75rem;
      color: #111827;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }
    .formula-tagline {
      color: #d13d4b;
      font-weight: 600;
      margin-bottom: 0.75rem;
      font-size: 1.05rem;
    }
    .formula-description {
      color: #6b7280;
      font-size: 1rem;
    }
    .price-block {
      background: #fef2f2;
      padding: 1.5rem;
      border-radius: 8px;
      margin: 1.5rem 0;
      text-align: center;
    }
    .price {
      font-size: 2.5rem;
      color: #d13d4b;
      font-weight: 700;
      display: block;
    }
    .price-detail {
      color: #6b7280;
      margin-top: 0.5rem;
      font-size: 0.95rem;
    }
    .price-original {
      text-decoration: line-through;
      color: #9ca3af;
      margin-right: 0.5rem;
      font-size: 1.25rem;
    }
    .price-saving {
      color: #059669;
      font-weight: 600;
      font-size: 1rem;
    }
    .features-list {
      list-style: none;
      margin-left: 0;
    }
    .features-list li {
      padding-left: 1.75rem;
      position: relative;
      margin-bottom: 0.75rem;
    }
    .features-list li:before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #059669;
      font-weight: 700;
      font-size: 1.1rem;
    }
    .excluded-list li:before {
      content: '✗';
      color: #dc2626;
    }
    .highlight {
      background: #fef3c7;
      padding: 0.2rem 0.4rem;
      border-radius: 3px;
      font-weight: 600;
    }
    .badge {
      display: inline-block;
      background: #d13d4b;
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 600;
      margin-left: 0.5rem;
    }
    .badge-success { background: #059669; }
    .badge-warning { background: #f59e0b; }

    /* Steps/Etapes */
    .step-card {
      border-left: 4px solid #d13d4b;
      padding: 1.5rem;
      margin: 1.5rem 0;
      background: #f9fafb;
      page-break-inside: avoid;
    }
    .step-header {
      display: flex;
      align-items: baseline;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    .step-number {
      font-size: 2rem;
      font-weight: 700;
      color: #d13d4b;
      line-height: 1;
    }
    .step-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #111827;
      flex: 1;
    }
    .step-duration {
      background: #e5e7eb;
      padding: 0.25rem 0.75rem;
      border-radius: 12px;
      font-size: 0.85rem;
      color: #4b5563;
      font-weight: 500;
    }

    /* Options/Packs */
    .pack-card {
      border: 2px solid #e5e7eb;
      border-radius: 10px;
      padding: 1.75rem;
      margin: 1.5rem 0;
      background: white;
      page-break-inside: avoid;
    }
    .pack-card.recommended {
      border-color: #d13d4b;
      box-shadow: 0 4px 12px rgba(209, 61, 75, 0.15);
    }
    .pack-header {
      display: flex;
      justify-content: space-between;
      align-items: start;
      margin-bottom: 1rem;
    }
    .pack-name {
      font-size: 1.35rem;
      font-weight: 600;
      color: #111827;
    }
    .pack-price {
      font-size: 1.75rem;
      font-weight: 700;
      color: #d13d4b;
      text-align: right;
    }

    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1.5rem 0;
      font-size: 0.95rem;
    }
    th, td {
      border: 1px solid #d1d5db;
      padding: 0.875rem;
      text-align: left;
    }
    th {
      background: #d13d4b;
      color: white;
      font-weight: 600;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    tr:nth-child(even) { background: #f9fafb; }
    td { color: #4b5563; }

    /* Brief sections */
    .brief-section {
      margin: 2rem 0;
      padding: 1.5rem;
      background: #f9fafb;
      border-left: 4px solid #d13d4b;
    }
    .brief-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #111827;
      margin-bottom: 1rem;
    }

    /* Payment */
    .payment-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
      margin: 1.5rem 0;
    }
    .payment-item {
      background: #fef2f2;
      padding: 1.25rem;
      border-radius: 8px;
      text-align: center;
      border: 1px solid #fecaca;
    }
    .payment-percent {
      font-size: 1.5rem;
      font-weight: 700;
      color: #d13d4b;
      display: block;
    }
    .payment-amount {
      font-size: 1.75rem;
      font-weight: 700;
      color: #111827;
      margin: 0.5rem 0;
    }
    .payment-when {
      font-size: 0.85rem;
      color: #6b7280;
    }

    /* Alert boxes */
    .alert {
      padding: 1rem 1.25rem;
      border-radius: 6px;
      margin: 1rem 0;
      border-left: 4px solid;
    }
    .alert-warning {
      background: #fffbeb;
      border-color: #f59e0b;
      color: #92400e;
    }
    .alert-info {
      background: #eff6ff;
      border-color: #3b82f6;
      color: #1e40af;
    }
    .alert-success {
      background: #f0fdf4;
      border-color: #059669;
      color: #065f46;
    }

    /* Footer */
    footer {
      margin-top: 3rem;
      padding: 2rem 3rem;
      background: #1f2937;
      color: #d1d5db;
      text-align: center;
    }
    footer strong { color: #fff; }

    /* Print styles */
    @media print {
      body { background: white; font-size: 11pt; }
      .container { padding: 0; }
      .section {
        page-break-inside: avoid;
        box-shadow: none;
        padding: 1.5rem;
        margin-bottom: 1.5rem;
      }
      .hero-stats { display: none; }
      h2 { page-break-after: avoid; }
      .formula-card, .pack-card, .step-card { page-break-inside: avoid; }
    }
  </style>
</head>
<body>

  <!-- HERO SECTION -->
  <div class="hero">
    ${project.content?.hero?.badge ? `<div class="hero-badge">${project.content.hero.badge}</div>` : ''}
    <h1>${project.title || ''}</h1>
    <p class="hero-subtitle">${project.content?.hero?.subtitle || project.subtitle || ''}</p>
    ${project.content?.hero?.description ? `<p style="max-width: 800px; margin: 0 auto; opacity: 0.95;">${project.content.hero.description}</p>` : ''}

    ${project.content?.hero?.stats && project.content.hero.stats.length > 0 ? `
    <div class="hero-stats">
      ${project.content.hero.stats.map(stat => `
        <div class="stat">
          <span class="stat-value">${stat.value}</span>
          <span class="stat-label">${stat.label}</span>
        </div>
      `).join('')}
    </div>
    ` : ''}
  </div>

  <div class="container">

    <!-- SECTION PROJET -->
    ${project.content?.projet ? `
    <div class="section">
      <h2>📋 ${project.content.projet.title || 'Le Projet'}</h2>
      ${project.content.projet.intro ? `<p style="font-size: 1.1rem; font-weight: 500; color: #111827;">${project.content.projet.intro}</p>` : ''}

      ${project.content.projet.blocks && project.content.projet.blocks.length > 0 ? `
      <div class="card-grid">
        ${project.content.projet.blocks.map(block => `
          <div class="card">
            <h4>${block.title}</h4>
            ${block.items && block.items.length > 0 ? `
            <ul>
              ${block.items.map(item => `<li>${item}</li>`).join('')}
            </ul>
            ` : ''}
          </div>
        `).join('')}
      </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- SECTION BRIEF -->
    ${project.content?.brief ? `
    <div class="section">
      <h2>📝 ${project.content.brief.title || 'Brief Créatif & Stratégique'}</h2>
      ${project.content.brief.description ? `<p style="font-size: 1.05rem; color: #6b7280; margin-bottom: 2rem;">${project.content.brief.description}</p>` : ''}

      ${project.content.brief.sections ? Object.entries(project.content.brief.sections).map(([key, section]) => `
        <div class="brief-section">
          <h3 class="brief-title">${section.title}</h3>
          <div style="white-space: pre-wrap;">${section.content}</div>
        </div>
      `).join('') : ''}
    </div>
    ` : ''}

    <!-- SECTION ÉTAPES -->
    ${project.content?.etapes && project.content.etapes.items && project.content.etapes.items.length > 0 ? `
    <div class="section">
      <h2>🎬 ${project.content.etapes.title || 'Les Étapes'}</h2>
      ${project.content.etapes.description ? `<p style="font-size: 1.05rem; color: #6b7280; margin-bottom: 2rem;">${project.content.etapes.description}</p>` : ''}

      ${project.content.etapes.items.map(etape => `
        <div class="step-card">
          <div class="step-header">
            <span class="step-number">${String(etape.id).padStart(2, '0')}</span>
            <span class="step-title">${etape.title}</span>
            ${etape.duration ? `<span class="step-duration">${etape.duration}</span>` : ''}
          </div>
          ${etape.description ? `<p style="margin-bottom: 1rem; color: #4b5563;">${etape.description}</p>` : ''}
          ${etape.details && etape.details.length > 0 ? `
          <ul style="margin-top: 1rem;">
            ${etape.details.map(detail => `<li>${detail}</li>`).join('')}
          </ul>
          ` : ''}
          ${etape.deliverable ? `
          <div class="alert alert-success" style="margin-top: 1rem;">
            <strong>📦 Livrable :</strong> ${etape.deliverable}
          </div>
          ` : ''}
          ${etape.validation ? `
          <div class="alert alert-info" style="margin-top: 1rem;">
            <strong>✓ Validation :</strong> ${etape.validation}
          </div>
          ` : ''}
        </div>
      `).join('')}
    </div>
    ` : ''}

    <!-- SECTION FORMULES & TARIFS -->
    ${project.formules && project.formules.length > 0 ? `
    <div class="section">
      <h2>💰 Formules & Tarifs</h2>
      <p style="font-size: 1.05rem; margin-bottom: 2rem;"><strong>Taux Journalier Moyen (TJM) :</strong> 400€/jour</p>

      ${project.formules.map(formule => `
        <div class="formula-card">
          <div class="formula-header">
            <div class="formula-name">
              ${formule.name}
              ${formule.recommended ? '<span class="badge badge-success">Recommandé</span>' : ''}
            </div>
            <div class="formula-tagline">${formule.tagline || ''}</div>
            <div class="formula-description">${formule.description || ''}</div>
          </div>

          <!-- Prix -->
          <div class="price-block">
            ${formule.pricing?.basePrice && formule.pricing.discount ? `
            <div style="margin-bottom: 0.5rem;">
              <span class="price-original">${formatPrice(formule.pricing.basePrice)}</span>
              <span class="price-saving">-${formule.pricing.discount}%</span>
            </div>
            ` : ''}
            <span class="price">${formatPrice(formule.pricing?.finalPrice || 0)}</span>
            <div class="price-detail">
              ${formule.pricing?.days || 0} jours × ${formatPrice(formule.pricing?.tjm || 400)}/jour
            </div>
          </div>

          <!-- Paiements -->
          ${formule.payment ? `
          <h4 style="margin-top: 2rem;">💳 Échelonnement des paiements</h4>
          <div class="payment-grid">
            ${formule.payment.deposit ? `
            <div class="payment-item">
              <span class="payment-percent">${formule.payment.deposit.percent}%</span>
              <div class="payment-amount">${formatPrice(formule.payment.deposit.amount)}</div>
              <div class="payment-when">${formule.payment.deposit.when}</div>
            </div>
            ` : ''}
            ${formule.payment.milestone1 ? `
            <div class="payment-item">
              <span class="payment-percent">${formule.payment.milestone1.percent}%</span>
              <div class="payment-amount">${formatPrice(formule.payment.milestone1.amount)}</div>
              <div class="payment-when">${formule.payment.milestone1.when}</div>
            </div>
            ` : ''}
            ${formule.payment.milestone2 ? `
            <div class="payment-item">
              <span class="payment-percent">${formule.payment.milestone2.percent}%</span>
              <div class="payment-amount">${formatPrice(formule.payment.milestone2.amount)}</div>
              <div class="payment-when">${formule.payment.milestone2.when}</div>
            </div>
            ` : ''}
            ${formule.payment.final ? `
            <div class="payment-item">
              <span class="payment-percent">${formule.payment.final.percent}%</span>
              <div class="payment-amount">${formatPrice(formule.payment.final.amount)}</div>
              <div class="payment-when">${formule.payment.final.when}</div>
            </div>
            ` : ''}
          </div>
          ` : ''}

          <!-- Prestations incluses -->
          ${formule.features?.included && formule.features.included.length > 0 ? `
          <h4 style="margin-top: 2rem;">✓ Prestations incluses</h4>
          <ul class="features-list">
            ${formule.features.included.map(f => `
              <li>${f.highlight ? `<span class="highlight">${f.text}</span>` : f.text}</li>
            `).join('')}
          </ul>
          ` : ''}

          <!-- Prestations exclues -->
          ${formule.features?.excluded && formule.features.excluded.length > 0 ? `
          <h4 style="margin-top: 1.5rem;">✗ Non inclus</h4>
          <ul class="features-list excluded-list">
            ${formule.features.excluded.map(f => `<li>${f.text}</li>`).join('')}
          </ul>
          ` : ''}

          <!-- Implication client -->
          ${formule.involvement ? `
          <div class="alert alert-info" style="margin-top: 2rem;">
            <h4 style="margin: 0 0 0.5rem;">👥 Votre implication</h4>
            <p style="margin: 0;"><strong>Niveau :</strong> ${formule.involvement.level === 'intensive' ? 'Intensive' : formule.involvement.level === 'active' ? 'Active' : 'Minimale'}</p>
            ${formule.involvement.description ? `<p style="margin: 0.5rem 0 0;">${formule.involvement.description}</p>` : ''}
            ${formule.involvement.time ? `<p style="margin: 0.5rem 0 0;"><strong>Temps estimé :</strong> ${formule.involvement.time}</p>` : ''}
          </div>
          ` : ''}

          <!-- Clause spéciale -->
          ${formule.specialClause ? `
          <div class="alert alert-success" style="margin-top: 1.5rem;">
            <h4 style="margin: 0 0 0.5rem;">🛡️ ${formule.specialClause.title}</h4>
            <p style="margin: 0;">${formule.specialClause.description}</p>
            ${formule.specialClause.checkpoint ? `<p style="margin: 0.5rem 0 0;"><strong>Checkpoint :</strong> ${formule.specialClause.checkpoint}</p>` : ''}
          </div>
          ` : ''}

          <!-- Idéal pour -->
          ${formule.ideal ? `
          <div style="margin-top: 1.5rem; padding: 1rem; background: #f0fdf4; border-radius: 6px; border-left: 4px solid #059669;">
            <strong style="color: #065f46;">✨ Idéal pour :</strong> ${formule.ideal}
          </div>
          ` : ''}

          <!-- Avertissements -->
          ${formule.warnings && formule.warnings.length > 0 ? `
          <div class="alert alert-warning" style="margin-top: 1.5rem;">
            <strong>⚠️ À noter :</strong>
            <ul style="margin: 0.5rem 0 0; padding-left: 1.5rem;">
              ${formule.warnings.map(w => `<li style="margin: 0.25rem 0;">${w}</li>`).join('')}
            </ul>
          </div>
          ` : ''}

          <!-- Bénéfices -->
          ${formule.benefits && formule.benefits.length > 0 ? `
          <div style="margin-top: 1.5rem;">
            <h4>🎯 Points forts</h4>
            <ul>
              ${formule.benefits.map(b => `<li>${b}</li>`).join('')}
            </ul>
          </div>
          ` : ''}
        </div>
      `).join('')}
    </div>
    ` : ''}

    <!-- SECTION OPTIONS/PACKS -->
    ${project.content?.options ? `
    <div class="section">
      <h2>🎥 ${project.content.options.title || 'Options Additionnelles'}</h2>
      ${project.content.options.subtitle ? `<h3 style="color: #6b7280; font-weight: 500; margin-top: 0;">${project.content.options.subtitle}</h3>` : ''}
      ${project.content.options.description ? `<p style="font-size: 1.05rem; margin-bottom: 2rem;">${project.content.options.description}</p>` : ''}

      ${project.content.options.packs && project.content.options.packs.length > 0 ? project.content.options.packs.map(pack => `
        <div class="pack-card ${pack.recommended ? 'recommended' : ''}">
          <div class="pack-header">
            <div>
              <div class="pack-name">
                ${pack.name}
                ${pack.recommended ? '<span class="badge badge-success">Recommandé</span>' : ''}
              </div>
              ${pack.description ? `<p style="color: #6b7280; margin-top: 0.5rem; font-size: 0.95rem;">${pack.description}</p>` : ''}
            </div>
            <div>
              <div class="pack-price">${formatPrice(pack.price)}</div>
              ${pack.originalPrice ? `
              <div style="text-align: right; margin-top: 0.25rem;">
                <span style="text-decoration: line-through; color: #9ca3af; font-size: 1rem;">${formatPrice(pack.originalPrice)}</span>
                ${pack.saving ? `<span style="color: #059669; font-weight: 600; font-size: 0.9rem; margin-left: 0.5rem;">-${formatPrice(pack.saving)}</span>` : ''}
              </div>
              ` : ''}
            </div>
          </div>
          ${pack.duration ? `<div style="color: #6b7280; margin-bottom: 1rem;"><strong>Durée de réalisation :</strong> ${pack.duration}</div>` : ''}
          ${pack.livrables && pack.livrables.length > 0 ? `
          <h4 style="margin-top: 1rem;">📦 Livrables</h4>
          <ul>
            ${pack.livrables.map(l => `<li>${l}</li>`).join('')}
          </ul>
          ` : ''}
          ${pack.highlight ? `
          <div class="alert alert-success" style="margin-top: 1rem;">
            ${pack.highlight}
          </div>
          ` : ''}
        </div>
      `).join('') : ''}

      ${project.content.options.surMesure ? `
      <div style="margin-top: 2rem; padding: 1.5rem; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb;">
        <h3>${project.content.options.surMesure.title}</h3>
        ${project.content.options.surMesure.description ? `<p>${project.content.options.surMesure.description}</p>` : ''}
        ${project.content.options.surMesure.items && project.content.options.surMesure.items.length > 0 ? `
        <table>
          <thead>
            <tr>
              <th>Format</th>
              <th>Tarif</th>
            </tr>
          </thead>
          <tbody>
            ${project.content.options.surMesure.items.map(item => `
            <tr>
              <td>${item.format}</td>
              <td><strong>${formatPrice(item.price)}</strong></td>
            </tr>
            `).join('')}
          </tbody>
        </table>
        ` : ''}
      </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- SECTION PLANNING -->
    ${project.planning && project.planning.milestones && project.planning.milestones.length > 0 ? `
    <div class="section">
      <h2>📅 Planning Prévisionnel</h2>
      ${project.planning.duration ? `
      <p style="font-size: 1.05rem; margin-bottom: 2rem;">
        <strong>Durée totale :</strong> ${project.planning.duration.months} mois
        (${formatDate(project.planning.duration.start)} - ${formatDate(project.planning.duration.end)})
      </p>
      ` : ''}

      ${project.planning.vacations && project.planning.vacations.length > 0 ? `
      <div class="alert alert-warning" style="margin-bottom: 2rem;">
        <strong>⚠️ Périodes de congés</strong>
        <ul style="margin: 0.5rem 0 0; padding-left: 1.5rem;">
          ${project.planning.vacations.map(v => `
            <li>${formatDate(v.start)} au ${formatDate(v.end)}</li>
          `).join('')}
        </ul>
        <p style="margin: 0.5rem 0 0;">Les livrables sont planifiés en dehors de ces périodes.</p>
      </div>
      ` : ''}

      <table>
        <thead>
          <tr>
            <th style="width: 20%;">Date</th>
            <th style="width: 35%;">Jalon</th>
            <th style="width: 20%;">Type</th>
            <th style="width: 25%;">Détail</th>
          </tr>
        </thead>
        <tbody>
          ${project.planning.milestones.map(m => `
          <tr style="${m.critical ? 'background: #fef2f2 !important; font-weight: 600;' : ''}">
            <td>${formatDate(m.date)}</td>
            <td>
              ${m.label}
              ${m.critical ? '<span class="badge badge-warning" style="margin-left: 0.5rem; font-size: 0.7rem;">CRITIQUE</span>' : ''}
            </td>
            <td>
              ${m.type === 'payment' ? '💳 Paiement' : ''}
              ${m.type === 'deliverable' ? '📦 Livrable' : ''}
              ${m.type === 'meeting' ? '👥 Réunion' : ''}
              ${m.type === 'milestone' ? '🎯 Jalon' : ''}
            </td>
            <td style="font-size: 0.85rem; color: #6b7280;">
              ${m.duration ? `Durée : ${m.duration}` : ''}
              ${m.amount && project.formules && project.formules[0] ? formatPrice(m.amount[project.formules[0].id] || 0) : ''}
              ${m.warning ? `⚠️ ${m.warning}` : ''}
            </td>
          </tr>
          `).join('')}
        </tbody>
      </table>

      ${project.planning.phases && project.planning.phases.length > 0 ? `
      <h3 style="margin-top: 3rem;">Phases du projet</h3>
      ${project.planning.phases.map(phase => `
        <div class="card" style="margin: 1.5rem 0;">
          <h4>${phase.name}</h4>
          <p style="color: #6b7280; margin-bottom: 1rem;">
            <strong>Période :</strong> ${formatDate(phase.start)} - ${formatDate(phase.end)}
          </p>
          ${phase.tasks && phase.tasks.length > 0 ? `
          <table style="font-size: 0.9rem;">
            <thead>
              <tr>
                <th>Tâche</th>
                <th>Responsable</th>
                <th>Durée</th>
              </tr>
            </thead>
            <tbody>
              ${phase.tasks.map(task => `
              <tr>
                <td>${task.name}</td>
                <td>${task.owner}</td>
                <td>${task.duration}</td>
              </tr>
              `).join('')}
            </tbody>
          </table>
          ` : ''}
        </div>
      `).join('')}
      ` : ''}
    </div>
    ` : ''}

    <!-- SECTION MARCHÉ (si enabled) -->
    ${project.content?.marche && project.sections?.find(s => s.id === 'marche')?.enabled ? `
    <div class="section">
      <h2>📊 ${project.content.marche.title || 'Contexte Marché'}</h2>
      ${project.content.marche.subtitle ? `<p style="font-size: 1.05rem; color: #6b7280; margin-bottom: 2rem;">${project.content.marche.subtitle}</p>` : ''}

      ${project.content.marche.sections ? Object.entries(project.content.marche.sections).map(([key, section]) => `
        <div style="margin: 2.5rem 0;">
          <h3>${section.title}</h3>
          ${section.intro ? `<p style="color: #6b7280; margin-bottom: 1rem;">${section.intro}</p>` : ''}

          ${section.data && Array.isArray(section.data) ? section.data.map(item => `
            <div class="card" style="margin: 1rem 0;">
              ${item.type ? `<h4>${item.type}</h4>` : ''}
              ${item.profil ? `<h4>${item.profil}</h4>` : ''}
              ${item.regle ? `<h4>${item.regle}</h4>` : ''}
              ${item.budgetTotal ? `<p><strong>Budget total :</strong> ${item.budgetTotal}</p>` : ''}
              ${item.postProd ? `<p><strong>Post-production :</strong> ${item.postProd}</p>` : ''}
              ${item.coutMinute ? `<p><strong>Coût/minute :</strong> ${item.coutMinute}</p>` : ''}
              ${item.tjm ? `<p><strong>TJM :</strong> ${item.tjm}</p>` : ''}
              ${item.estimation ? `<p><strong>Estimation :</strong> ${item.estimation}</p>` : ''}
              ${item.detail ? `<p style="color: #6b7280; font-size: 0.9rem;">${item.detail}</p>` : ''}
              ${item.application ? `<p><strong>${item.application}</strong></p>` : ''}
              ${item.source ? `
              <div style="margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid #e5e7eb; font-size: 0.85rem; color: #6b7280;">
                <strong>Source :</strong> ${item.source.name}
                ${item.source.citation ? `<br><em>"${item.source.citation}"</em>` : ''}
              </div>
              ` : ''}
              ${item.sources && Array.isArray(item.sources) ? `
              <div style="margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid #e5e7eb; font-size: 0.85rem; color: #6b7280;">
                <strong>Sources :</strong> ${item.sources.map(s => s.name).join(', ')}
              </div>
              ` : ''}
            </div>
          `).join('') : ''}

          ${section.comparaisons && Array.isArray(section.comparaisons) ? `
          <table style="margin-top: 1.5rem;">
            <thead>
              <tr>
                <th>Référence</th>
                <th>Prix standard</th>
                <th>Notre tarif</th>
                <th>Économie</th>
              </tr>
            </thead>
            <tbody>
              ${section.comparaisons.map(comp => `
              <tr>
                <td>${comp.reference}</td>
                <td>${comp.prix}</td>
                <td style="color: #d13d4b; font-weight: 600;">${comp.notre}</td>
                <td style="color: #059669; font-weight: 600;">${comp.economie}</td>
              </tr>
              `).join('')}
            </tbody>
          </table>
          ${section.explication ? `
          <div class="alert alert-info" style="margin-top: 1rem;">
            ${section.explication}
          </div>
          ` : ''}
          ` : ''}
        </div>
      `).join('') : ''}

      ${project.content.marche.sources && project.content.marche.sources.length > 0 ? `
      <div style="margin-top: 3rem; padding: 1.5rem; background: #f9fafb; border-radius: 8px;">
        <h4>📚 Sources & Références</h4>
        <ul style="font-size: 0.9rem;">
          ${project.content.marche.sources.map(source => `
            <li>
              <strong>${source.title}</strong> (${source.date})<br>
              <span style="color: #6b7280;">${source.description}</span>
            </li>
          `).join('')}
        </ul>
      </div>
      ` : ''}
    </div>
    ` : ''}

    <!-- SECTION CONTACT -->
    ${project.content?.contact ? `
    <div class="section">
      <h2>📞 ${project.content.contact.title || 'Contact'}</h2>
      ${project.content.contact.description ? `<p style="font-size: 1.05rem; margin-bottom: 2rem;">${project.content.contact.description}</p>` : ''}

      ${project.content.contact.steps && project.content.contact.steps.length > 0 ? `
      <div class="card-grid">
        ${project.content.contact.steps.map(step => `
          <div class="card">
            <div style="font-size: 2rem; font-weight: 700; color: #d13d4b; margin-bottom: 0.5rem;">${step.number}</div>
            <h4 style="margin-top: 0;">${step.title}</h4>
            <p style="margin: 0; color: #6b7280;">${step.description}</p>
          </div>
        `).join('')}
      </div>
      ` : ''}

      ${project.content.contact.cta ? `
      <div style="margin-top: 2rem; padding: 2rem; background: #fef2f2; border-radius: 8px; text-align: center;">
        <h3 style="margin-top: 0; color: #d13d4b;">Restons en contact</h3>
        ${project.content.contact.cta.email ? `
        <p style="font-size: 1.1rem; margin: 0.5rem 0;">
          <strong>Email :</strong> <a href="mailto:${project.content.contact.cta.email}" style="color: #d13d4b; text-decoration: none;">${project.content.contact.cta.email}</a>
        </p>
        ` : ''}
        ${project.content.contact.cta.phone ? `
        <p style="font-size: 1.1rem; margin: 0.5rem 0;">
          <strong>Téléphone :</strong> <a href="tel:${project.content.contact.cta.phone.replace(/\s/g, '')}" style="color: #d13d4b; text-decoration: none;">${project.content.contact.cta.phone}</a>
        </p>
        ` : ''}
        ${project.content.contact.cta.calendly ? `
        <p style="font-size: 1.1rem; margin: 0.5rem 0;">
          <strong>Réserver un créneau :</strong> <a href="${project.content.contact.cta.calendly}" style="color: #d13d4b; text-decoration: none;">Calendly</a>
        </p>
        ` : ''}
      </div>
      ` : ''}
    </div>
    ` : ''}

  </div>

  <!-- FOOTER -->
  <footer>
    <p style="font-size: 1.2rem; margin-bottom: 0.5rem;"><strong>Cahier de Préconisation</strong></p>
    <p>Patrick Sanchez - Monteur Vidéo Freelance</p>
    <p style="margin-top: 0.5rem;">${formatDate(new Date().toISOString())}</p>
    <p style="margin-top: 1.5rem; font-size: 0.85rem; opacity: 0.8;">
      Ce document peut être imprimé en PDF depuis votre navigateur<br>
      (Fichier › Imprimer › Enregistrer en PDF)
    </p>
  </footer>

</body>
</html>`;

    console.log('[exportHTML] Creating blob...');
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${project.slug || 'cahier'}-preconisation.html`;

    console.log('[exportHTML] Triggering download:', link.download);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('[exportHTML] Export completed successfully');
  } catch (error) {
    console.error('[exportHTML] Export failed:', error);
    alert(`Erreur lors de l'export HTML : ${error.message}`);
  }
}
